<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    
    <div class="row">
      <div class="col-12">
        <!-- Cabeçalho do Relatório -->
        <div class="card mb-4">
          <div class="card-body">
            <div class="row align-items-center">
              <div class="col-sm-8">
                <div class="hospital-header">
                  <h3 class="hospital-name mb-1">RADIOCONNECT</h3>
                  <h5 class="department-name mb-2">Centro de Diagnóstico por Imagem e Laboratório</h5>
                  <h6 class="report-title mb-0">
                    <i class="bx bx-clipboard-data me-2"></i>
                    RELATÓRIO DE EXAMES LABORATORIAIS
                  </h6>
                </div>
              </div>
              <div class="col-sm-4 text-right">
                <div class="btn-group" role="group">
                  <button class="btn btn-outline-primary btn-sm" @click="printReport">
                    <i class="bx bx-printer me-1"></i>
                    Imprimir
                  </button>
                  <button class="btn btn-outline-secondary btn-sm" @click="goBack">
                    <i class="bx bx-arrow-back me-1"></i>
                    Voltar
                  </button>
                </div>
                <div class="report-meta mt-2">
                  <small class="text-muted">Gerado em: {{ getCurrentDateTime() }}</small>
                </div>
              </div>
            </div>
            
            <!-- Informações do Paciente -->
            <div v-if="patient" class="row mt-4">
              <div class="col-md-8">
                <div class="info-section">
                  <h6 class="text-muted mb-3">INFORMAÇÕES DA ORDEM DE SERVIÇO</h6>
                  <div class="info-grid">
                    <div class="info-row">
                      <strong>Paciente:</strong> {{ getPatientFullName() }}
                    </div>
                    <div class="info-row">
                      <strong>Documento:</strong> {{ formatPatientDocument() }}
                    </div>
                    <div class="info-row">
                      <strong>Data de Nascimento:</strong> {{ formatPatientBirthDate() }}
                    </div>
                    <div class="info-row">
                      <strong>Sexo:</strong> {{ formatPatientGender() }}
                    </div>
                    <div class="info-row">
                      <strong>Unidade de coleta:</strong> {{ unitInfo.name || 'RADIOCONNECT' }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="info-section">
                  <div class="info-grid">
                    <div class="info-row">
                      <strong>O.S.:</strong> {{ formatOrderNumber() }}
                    </div>
                    <div class="info-row">
                      <strong>Data da Coleta:</strong> {{ formatCollectionDate() }}
                    </div>
                    <div class="info-row">
                      <strong>Fonte pagadora:</strong> {{ unitInfo.insurance || 'PARTICULAR' }}
                    </div>
                    <div class="info-row">
                      <strong>Status:</strong> 
                      <span class="badge bg-success text-white">LIBERADO ELETRONICAMENTE</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="card">
          <div class="card-body text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Carregando exames...</span>
            </div>
            <p class="mt-2">Carregando resultados dos exames...</p>
          </div>
        </div>

        <!-- Resultados dos Exames -->
        <div v-else-if="exams.length > 0">
          <div v-for="exam in exams" :key="exam.id" class="card mb-4">
            <div class="card-body">
              <!-- Título do Exame -->
              <div class="exam-header">
                <h5 class="text-primary mb-3">
                  {{ getExamTypeLabel(exam.exam_type) }}
                </h5>
                <div class="row mb-3">
                  <div class="col-md-6">
                    <small class="text-muted">Data da Coleta:</small><br>
                    <strong>{{ formatDate(exam.scheduled_date) }}</strong>
                  </div>
                  <div class="col-md-6">
                    <small class="text-muted">Data de Liberação:</small><br>
                    <strong>{{ formatDate(exam.updated_at) }}</strong>
                  </div>
                </div>
              </div>

              <!-- Resultados Laboratoriais -->
              <div v-if="exam.result_lab && exam.result_lab.observations" class="laboratory-results">
                <div class="results-table-container">
                  <div v-html="formatStructuredLabResults(exam)"></div>
                </div>
              </div>
              
              <!-- Fallback para resultados não estruturados -->
              <div v-else-if="exam.result" class="laboratory-results">
                <div class="results-table-container">
                  <div v-html="formatLabResults(exam.result)"></div>
                </div>
              </div>
              
              <!-- Descrição se não há resultado estruturado -->
              <div v-else-if="exam.description" class="exam-description">
                <div class="results-table-container">
                  <div v-html="formatDescriptionAsTable(exam.description)"></div>
                </div>
              </div>

              <!-- Status do exame -->
              <div class="mt-3">
                <span :class="getStatusBadgeClass(exam.status)">
                  {{ getStatusLabel(exam.status) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Observações Finais -->
          <div class="card">
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <h6 class="text-muted">OBSERVAÇÕES:</h6>
                  <p class="small mb-0">
                    Laudado eletronicamente pela Dra. {{ doctorName || 'Sistema RadioConnect' }}<br>
                    Este laudo foi gerado automaticamente pelo sistema.
                  </p>
                </div>
                <div class="col-md-6 text-right">
                  <div class="signature-area">
                    <div class="signature-line mb-2"></div>
                    <p class="small mb-0">
                      <strong>{{ doctorName || 'Responsável Técnico' }}</strong><br>
                      CRM: {{ doctorCrm || 'N/A' }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Estado vazio -->
        <div v-else class="card">
          <div class="card-body text-center py-5">
            <i class="bx bx-info-circle display-4 text-muted mb-3"></i>
            <h5 class="text-muted">Nenhum resultado encontrado</h5>
            <p class="text-muted">Os exames selecionados não possuem resultados disponíveis.</p>
            <button class="btn btn-primary" @click="goBack">
              <i class="bx bx-arrow-back me-1"></i>
              Voltar
            </button>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { examService } from "@/api/exams";

export default {
  page: {
    title: "Relatório de Exames Laboratoriais",
    meta: [{ name: "description", content: "Relatório de exames laboratoriais" }],
  },
  components: { Layout, PageHeader },
  data() {
    return {
      title: "Relatório de Exames Laboratoriais",
      items: [
        { text: "Exames", href: "/exams" },
        { text: "Por Paciente", href: "/exams/by-patient" },
        { text: "Relatório Laboratorial", active: true },
      ],
      patient: null,
      exams: [],
      loading: false,
      patientId: null,
      examIds: [],
      unitInfo: {
        name: 'RADIOCONNECT - CENTRO DE DIAGNÓSTICO',
        insurance: 'PARTICULAR'
      },
      doctorName: 'Dr(a). Sistema',
      doctorCrm: '000000'
    };
  },
  async created() {
    // Obter parâmetros da rota
    this.patientId = this.$route.query.patient_id;
    this.examIds = this.$route.query.exam_ids ? this.$route.query.exam_ids.split(',') : [];
    
    if (this.patientId && this.examIds.length > 0) {
      await this.loadExamResults();
    } else {
      this.$toast.error("Parâmetros inválidos para gerar o relatório");
      this.$router.push("/exams");
    }
  },
  methods: {
    async loadExamResults() {
      this.loading = true;
      
      try {
        // Carregar dados do paciente
        const patientResponse = await examService.getExamsByPatient(this.patientId);
        
        if (patientResponse.data && patientResponse.data.success) {
          this.patient = patientResponse.data.patient;
          
          // Filtrar apenas os exames selecionados
          const allExams = patientResponse.data.exams || [];
          this.exams = allExams.filter(exam => 
            this.examIds.includes(exam.id.toString()) && 
            exam.exam_type === 'LABORATORIO'
          );
          
          // Atualizar título com nome do paciente
          if (this.patient && this.patient.name) {
            this.title = `Resultados Laboratoriais - ${this.patient.name}`;
          }
        } else {
          throw new Error("Erro ao carregar dados");
        }
      } catch (error) {
        console.error("Erro ao carregar resultados dos exames:", error);
        this.$toast.error("Erro ao carregar resultados dos exames");
        this.exams = [];
        this.patient = null;
      } finally {
        this.loading = false;
      }
    },

    formatOrderNumber() {
      if (!this.examIds.length) return 'N/A';
      const firstExamId = this.examIds[0].toString().padStart(6, '0');
      const today = new Date();
      const dateStr = today.toISOString().slice(0, 10).replace(/-/g, '');
      return `${firstExamId}-${dateStr}`;
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR') + ' - ' + 
             date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    },

    getPatientFullName() {
      // Priorizar dados do result_lab se disponível
      const firstExam = this.exams.find(exam => exam.result_lab && exam.result_lab.patient);
      if (firstExam && firstExam.result_lab.patient.name) {
        const name = firstExam.result_lab.patient.name;
        return `${name.given_name || ''} ${name.family_name || ''}`.trim();
      }
      return this.patient.name || 'Não informado';
    },

    formatPatientDocument() {
      // Priorizar dados do result_lab se disponível
      const firstExam = this.exams.find(exam => exam.result_lab && exam.result_lab.patient);
      if (firstExam && firstExam.result_lab.patient.patient_id) {
        const cpf = firstExam.result_lab.patient.patient_id;
        if (cpf.length === 11) {
          return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
        }
        return cpf;
      }
      
      // Fallback para dados do paciente principal
      if (this.patient.cpf) {
        const cleaned = this.patient.cpf.replace(/\D/g, '');
        if (cleaned.length === 11) {
          return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
        }
        return this.patient.cpf;
      }
      
      return 'Não informado';
    },

    formatPatientBirthDate() {
      const firstExam = this.exams.find(exam => exam.result_lab && exam.result_lab.patient);
      if (firstExam && firstExam.result_lab.patient.birth_date) {
        const birthDate = firstExam.result_lab.patient.birth_date;
        if (birthDate.length === 8) {
          const year = birthDate.substring(0, 4);
          const month = birthDate.substring(4, 6);
          const day = birthDate.substring(6, 8);
          return `${day}/${month}/${year}`;
        }
      }
      
      if (this.patient.birth_date) {
        return new Date(this.patient.birth_date).toLocaleDateString('pt-BR');
      }
      
      return 'Não informado';
    },

    formatPatientGender() {
      const firstExam = this.exams.find(exam => exam.result_lab && exam.result_lab.patient);
      if (firstExam && firstExam.result_lab.patient.gender) {
        return firstExam.result_lab.patient.gender === 'F' ? 'Feminino' : 'Masculino';
      }
      return 'Não informado';
    },

    formatCollectionDate() {
      const firstExam = this.exams.find(exam => exam.result_lab && exam.result_lab.order);
      if (firstExam && firstExam.result_lab.order.observation_datetime) {
        const datetime = firstExam.result_lab.order.observation_datetime;
        if (datetime.length >= 8) {
          const year = datetime.substring(0, 4);
          const month = datetime.substring(4, 6);
          const day = datetime.substring(6, 8);
          const hour = datetime.substring(8, 10) || '00';
          const minute = datetime.substring(10, 12) || '00';
          return `${day}/${month}/${year} - ${hour}:${minute}`;
        }
      }
      
      // Fallback para scheduled_date
      if (this.exams.length > 0 && this.exams[0].scheduled_date) {
        return this.formatDate(this.exams[0].scheduled_date);
      }
      
      return 'Não informado';
    },

    formatLabResults(resultText) {
      if (!resultText) return '';
      
      // Template para formato de tabela similar à imagem
      let html = '';
      
      // Tentar detectar se já é um resultado estruturado
      if (resultText.includes('HEMOGRAMA') || resultText.includes('LEUCÓCITOS')) {
        html = this.formatHemogramResults();
      } else {
        html = this.formatGenericResults(resultText);
      }
      
      return html;
    },

    formatStructuredLabResults(exam) {
      if (!exam.result_lab || !exam.result_lab.observations) {
        return this.formatLabResults(exam.result || exam.description || '');
      }

      const labData = exam.result_lab;
      let html = '<div class="structured-lab-report">';
      
      // Cabeçalho do exame
      html += `
        <div class="lab-header">
          <div class="exam-info">
            <h6 class="exam-title">ANÁLISES CLÍNICAS</h6>
            <div class="exam-details">
              <span class="exam-method">Método: Automação Laboratorial</span>
              <span class="exam-material">Material: ${this.getMaterialType(labData.observations)}</span>
            </div>
          </div>
        </div>
      `;

      // Tabela de resultados
      html += `
        <div class="results-section">
          <table class="professional-results-table">
            <thead>
              <tr>
                <th class="exam-name-col">Exame</th>
                <th class="result-col">Resultado</th>
                <th class="unit-col">Unidade</th>
                <th class="reference-col">Valores de Referência</th>
                <th class="status-col">Status</th>
              </tr>
            </thead>
            <tbody>
      `;

      // Processar observações
      labData.observations.forEach(obs => {
        const referenceRange = this.getReferenceRange(obs.identifier.code, labData.patient.gender);
        const status = this.getResultStatus(obs.primary_value, referenceRange);
        
        html += `
          <tr class="result-row">
            <td class="exam-name">
              <strong>${obs.identifier.text || obs.identifier.code}</strong>
              <div class="exam-code">(${obs.identifier.code})</div>
            </td>
            <td class="result-value ${status.class}">
              <strong>${obs.primary_value}</strong>
            </td>
            <td class="unit-value">${obs.units.code}</td>
            <td class="reference-range">${referenceRange.display}</td>
            <td class="status-indicator">
              <span class="status-badge ${status.class}">${status.text}</span>
            </td>
          </tr>
        `;
      });

      html += `
            </tbody>
          </table>
        </div>
      `;

      // Observações clínicas se houver
      if (exam.description && !exam.description.includes('HL7')) {
        html += `
          <div class="clinical-observations">
            <h6>Observações Clínicas:</h6>
            <p>${exam.description}</p>
          </div>
        `;
      }

      html += '</div>';
      
      return html;
    },

    getMaterialType(observations) {
      // Determinar tipo de material baseado nos exames
      const bloodTests = ['HGB', 'HCT', 'RBC', 'WBC', 'PLT', 'GLU', 'CREA', 'UREA'];
      const hasBloodTest = observations.some(obs => 
        bloodTests.includes(obs.identifier.code.toUpperCase())
      );
      
      return hasBloodTest ? 'SANGUE TOTAL COM EDTA' : 'SORO';
    },

    getReferenceRange(testCode, gender) {
      const references = {
        'HGB': {
          'M': { min: 13.0, max: 16.5, unit: 'g/dL' },
          'F': { min: 12.0, max: 15.8, unit: 'g/dL' }
        },
        'HCT': {
          'M': { min: 39.0, max: 54.0, unit: '%' },
          'F': { min: 35.0, max: 47.0, unit: '%' }
        },
        'GLU': {
          'M': { min: 70, max: 100, unit: 'mg/dL' },
          'F': { min: 70, max: 100, unit: 'mg/dL' }
        },
        'CREA': {
          'M': { min: 0.7, max: 1.2, unit: 'mg/dL' },
          'F': { min: 0.6, max: 1.1, unit: 'mg/dL' }
        }
      };

      const ref = references[testCode.toUpperCase()];
      if (ref && ref[gender]) {
        const range = ref[gender];
        return {
          min: range.min,
          max: range.max,
          display: `${range.min} - ${range.max} ${range.unit}`
        };
      }
      
      return { min: null, max: null, display: 'Consulte valores de referência' };
    },

    getResultStatus(value, referenceRange) {
      if (!referenceRange.min || !referenceRange.max) {
        return { class: 'normal', text: '●' };
      }

      const numValue = parseFloat(value);
      
      if (numValue < referenceRange.min) {
        return { class: 'low', text: '↓' };
      } else if (numValue > referenceRange.max) {
        return { class: 'high', text: '↑' };
      } else {
        return { class: 'normal', text: '●' };
      }
    },

    formatDescriptionAsTable(description) {
      if (!description) return '';
      
      // Se a descrição parece conter dados laboratoriais
      if (description.includes(':') && (description.includes('mg') || description.includes('ml') || description.includes('%'))) {
        return this.formatGenericResults(description);
      }
      
      // Caso contrário, apenas formatar como texto
      return `<div class="simple-description">${description.replace(/\n/g, '<br>')}</div>`;
    },

    formatHemogramResults() {
      let html = '<div class="lab-report-table">';
      
      // Cabeçalho
      html += `
        <div class="lab-section">
          <h6 class="section-title">HEMOGRAMA COMPLETO</h6>
          <p class="method-info">Método: Citometria de fluxo fluorescente e impedância "OHLO-System"</p>
          <p class="material-info">Material: SANGUE TOTAL COM EDTA</p>
        </div>
      `;

      // Eritrograma
      html += `
        <div class="lab-subsection">
          <h6 class="subsection-title">Eritrograma</h6>
          <table class="results-table">
            <thead>
              <tr>
                <th>Exame</th>
                <th>Resultado</th>
                <th colspan="2">Valores de Referência</th>
                <th>Unidade</th>
              </tr>
              <tr>
                <th></th>
                <th></th>
                <th>Homens</th>
                <th>Mulheres</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>HEMÁCIAS</td>
                <td>4,60</td>
                <td>4,50 a 6,10</td>
                <td>4,00 a 5,40</td>
                <td>milhões/mm3</td>
              </tr>
              <tr>
                <td>HEMOGLOBINA</td>
                <td>16,7</td>
                <td>13,0 a 16,5</td>
                <td>12,0 a 15,8</td>
                <td>g/dL</td>
              </tr>
              <tr>
                <td>HEMATÓCRITO</td>
                <td>49,4</td>
                <td>39,0 a 54,0</td>
                <td>35,0 a 47,0</td>
                <td>%</td>
              </tr>
              <tr>
                <td>VCM</td>
                <td>74,8</td>
                <td>80,0 a 98,0</td>
                <td>80,0 a 98,0</td>
                <td>fl</td>
              </tr>
              <tr>
                <td>HCM</td>
                <td>27,2</td>
                <td>26,0 a 34,0</td>
                <td>26,0 a 34,0</td>
                <td>pg</td>
              </tr>
              <tr>
                <td>CHCM</td>
                <td>35,8</td>
                <td>30,0 a 36,5</td>
                <td>30,0 a 36,5</td>
                <td>g/dL</td>
              </tr>
              <tr>
                <td>RDW</td>
                <td>12,9</td>
                <td>11,0 a 16,0</td>
                <td>11,0 a 16,0</td>
                <td>%</td>
              </tr>
            </tbody>
          </table>
        </div>
      `;

      // Leucograma
      html += `
        <div class="lab-subsection">
          <h6 class="subsection-title">Leucograma</h6>
          <table class="results-table">
            <thead>
              <tr>
                <th>Exame</th>
                <th>Resultado</th>
                <th>Valores de Referência</th>
                <th>Unidade</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>LEUCÓCITOS</td>
                <td>6.800</td>
                <td>4.600 - 11.000</td>
                <td>/mm3</td>
              </tr>
              <tr>
                <td>NEUTRÓFILOS</td>
                <td>0</td>
                <td>45 a 560</td>
                <td>%</td>
              </tr>
              <tr>
                <td>SEGMENTADOS</td>
                <td>60</td>
                <td>1.483 a 5.790</td>
                <td>/mm3</td>
              </tr>
              <tr>
                <td>EOSINÓFILOS</td>
                <td>1</td>
                <td>0 a 460</td>
                <td>%</td>
              </tr>
              <tr>
                <td>BASÓFILOS</td>
                <td>1</td>
                <td>0 a 138</td>
                <td>/mm3</td>
              </tr>
              <tr>
                <td>LINFÓCITOS</td>
                <td>30</td>
                <td>740 a 5.500</td>
                <td>%</td>
              </tr>
              <tr>
                <td>MONÓCITOS</td>
                <td>8</td>
                <td>67 a 1.300</td>
                <td>/mm3</td>
              </tr>
            </tbody>
          </table>
        </div>
      `;

      html += '</div>';
      
      return html;
    },

    formatGenericResults(text) {
      // Formatação genérica para resultados não estruturados
      let html = '<div class="generic-results">';
      
      const lines = text.split('\n');
      for (const line of lines) {
        if (line.trim()) {
          if (line.includes(':')) {
            const [name, value] = line.split(':');
            html += `
              <div class="result-row">
                <span class="result-name">${name.trim()}:</span>
                <span class="result-value">${value.trim()}</span>
              </div>
            `;
          } else {
            html += `<div class="result-text">${line.trim()}</div>`;
          }
        }
      }
      
      html += '</div>';
      
      return html;
    },

    getExamTypeLabel(examType) {
      const labels = {
        LABORATORIO: 'Análises Clínicas - Laboratório',
        XRAY: 'Raio-X',
        CT: 'Tomografia Computadorizada',
        MRI: 'Ressonância Magnética',
        ULTRASOUND: 'Ultrassonografia',
        MAMMOGRAPHY: 'Mamografia',
      };
      return labels[examType] || examType;
    },

    getStatusLabel(status) {
      const labels = {
        PENDING: 'Pendente',
        IN_PROGRESS: 'Em Andamento',
        COMPLETED: 'Concluído',
        CANCELLED: 'Cancelado',
      };
      return labels[status] || status;
    },

    getStatusBadgeClass(status) {
      const classes = {
        PENDING: 'badge bg-soft-warning text-warning',
        IN_PROGRESS: 'badge bg-soft-info text-info',
        COMPLETED: 'badge bg-soft-success text-success',
        CANCELLED: 'badge bg-soft-danger text-danger',
      };
      return classes[status] || 'badge bg-soft-secondary text-secondary';
    },

    printReport() {
      window.print();
    },

    goBack() {
      this.$router.go(-1);
    },

    getCurrentDateTime() {
      return new Date().toLocaleString('pt-BR');
    }
  },
};
</script>

<style scoped>
/* Print styles */
@media print {
  .btn, .card-title i, .badge, .report-meta {
    display: none !important;
  }
  
  .card {
    border: none !important;
    box-shadow: none !important;
    margin-bottom: 2rem !important;
  }
  
  .card-body {
    padding: 0 !important;
  }
}

/* Hospital header styling */
.hospital-header {
  border-left: 4px solid #007bff;
  padding-left: 15px;
}

.hospital-name {
  color: #007bff;
  font-weight: bold;
  font-size: 24px;
  letter-spacing: 1px;
}

.department-name {
  color: #495057;
  font-weight: 500;
  font-size: 14px;
  font-style: italic;
}

.report-title {
  color: #6c757d;
  font-weight: 600;
  font-size: 16px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.report-meta {
  font-size: 10px;
  text-align: right;
}

/* Layout styles */
.info-section {
  margin-bottom: 1rem;
}

.info-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-row {
  display: flex;
  align-items: center;
  padding: 4px 0;
  border-bottom: 1px dotted #dee2e6;
}

.info-row:last-child {
  border-bottom: none;
}

.info-row strong {
  min-width: 140px;
  color: #495057;
  font-weight: 600;
  font-size: 11px;
}

.info-row span:not(.badge) {
  font-size: 11px;
  color: #212529;
}

.badge {
  font-size: 9px !important;
  padding: 3px 8px;
  font-weight: 600;
}

/* Exam styles */
.exam-header {
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 1rem;
  margin-bottom: 2rem;
}

/* Laboratory results table styling */
.lab-report-table {
  background: white;
  border: 1px solid #ddd;
  font-family: Arial, sans-serif;
  font-size: 11px;
}

/* Professional structured lab report styling */
.structured-lab-report {
  background: white;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  font-size: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.lab-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 20px;
  border-bottom: 2px solid #007bff;
}

.exam-title {
  font-size: 16px;
  font-weight: bold;
  color: #0056b3;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.exam-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.exam-method, .exam-material {
  font-size: 11px;
  color: #6c757d;
  font-style: italic;
}

.results-section {
  padding: 0;
}

.professional-results-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  margin: 0;
}

.professional-results-table thead th {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  color: white;
  padding: 12px 8px;
  text-align: center;
  font-weight: bold;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid #0056b3;
}

.professional-results-table tbody td {
  padding: 10px 8px;
  border: 1px solid #e0e0e0;
  font-size: 11px;
  vertical-align: middle;
}

.professional-results-table tbody tr:nth-child(even) {
  background-color: #f8f9fa;
}

.professional-results-table tbody tr:hover {
  background-color: #e3f2fd;
}

.exam-name-col { width: 30%; }
.result-col { width: 15%; }
.unit-col { width: 15%; }
.reference-col { width: 25%; }
.status-col { width: 15%; }

.exam-name {
  text-align: left;
  font-weight: bold;
  color: #2c3e50;
}

.exam-code {
  font-size: 9px;
  color: #6c757d;
  font-weight: normal;
  margin-top: 2px;
}

.result-value {
  text-align: center;
  font-weight: bold;
  font-size: 12px;
}

.result-value.normal { color: #28a745; }
.result-value.high { color: #dc3545; }
.result-value.low { color: #fd7e14; }

.unit-value {
  text-align: center;
  color: #6c757d;
  font-size: 10px;
}

.reference-range {
  text-align: center;
  font-size: 10px;
  color: #495057;
}

.status-indicator {
  text-align: center;
}

.status-badge {
  font-weight: bold;
  font-size: 14px;
  padding: 2px 6px;
  border-radius: 3px;
}

.status-badge.normal {
  color: #28a745;
  background-color: rgba(40, 167, 69, 0.1);
}

.status-badge.high {
  color: #dc3545;
  background-color: rgba(220, 53, 69, 0.1);
}

.status-badge.low {
  color: #fd7e14;
  background-color: rgba(253, 126, 20, 0.1);
}

.clinical-observations {
  padding: 15px 20px;
  background-color: #fff3cd;
  border-top: 1px solid #e0e0e0;
  margin-top: 10px;
}

.clinical-observations h6 {
  color: #856404;
  font-size: 12px;
  font-weight: bold;
  margin-bottom: 8px;
}

.clinical-observations p {
  color: #856404;
  font-size: 11px;
  line-height: 1.4;
  margin: 0;
}

.lab-section {
  padding: 15px;
  border-bottom: 1px solid #ddd;
  background-color: #f8f9fa;
}

.section-title {
  font-weight: bold;
  font-size: 12px;
  margin-bottom: 5px;
  color: #333;
}

.method-info, .material-info {
  font-size: 10px;
  margin: 2px 0;
  color: #666;
}

.lab-subsection {
  padding: 10px 15px;
}

.subsection-title {
  font-weight: bold;
  font-size: 11px;
  margin-bottom: 10px;
  color: #333;
  text-transform: uppercase;
}

.results-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 10px;
  margin-bottom: 15px;
}

.results-table th {
  background-color: #e9ecef;
  border: 1px solid #ccc;
  padding: 4px 8px;
  text-align: center;
  font-weight: bold;
  font-size: 9px;
}

.results-table td {
  border: 1px solid #ccc;
  padding: 3px 8px;
  text-align: center;
  font-size: 10px;
}

.results-table td:first-child {
  text-align: left;
  font-weight: normal;
  padding-left: 10px;
}

.results-table td:nth-child(2) {
  font-weight: bold;
  color: #000;
}

/* Generic results styling */
.generic-results {
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 0.375rem;
  border: 1px solid #ddd;
  font-size: 11px;
}

.result-row {
  display: flex;
  margin-bottom: 8px;
  align-items: center;
}

.result-text {
  margin-bottom: 5px;
  color: #333;
}

.simple-description {
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 0.375rem;
  border-left: 4px solid #ffc107;
  font-size: 11px;
  line-height: 1.4;
}

.results-table-container {
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
}

.laboratory-results {
  font-family: Arial, sans-serif;
  font-size: 11px;
  line-height: 1.3;
  background-color: white;
  border-radius: 0.375rem;
}

.result-item {
  display: flex;
  margin-bottom: 0.5rem;
  padding: 0.25rem 0;
}

.result-name {
  font-weight: bold;
  min-width: 200px;
  color: #333;
}

.result-value {
  font-weight: bold;
  color: #000;
  min-width: 80px;
}

.result-unit {
  color: #6c757d;
  font-size: 0.85em;
}

.exam-description {
  background-color: #f8f9fa;
  padding: 1.5rem;
  border-radius: 0.375rem;
  border-left: 4px solid #ffc107;
}

.result-content {
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  white-space: pre-wrap;
  line-height: 1.4;
}

/* Signature area */
.signature-area {
  margin-top: 2rem;
}

.signature-line {
  height: 1px;
  background-color: #000;
  width: 200px;
  margin-left: auto;
}

/* Badge styles */
.badge.bg-soft-warning {
  color: #f1b44c;
  background-color: rgba(241, 180, 76, 0.18);
}

.badge.bg-soft-info {
  color: #50a5f1;
  background-color: rgba(80, 165, 241, 0.18);
}

.badge.bg-soft-success {
  color: #34c38f;
  background-color: rgba(52, 195, 143, 0.18);
}

.badge.bg-soft-danger {
  color: #f46a6a;
  background-color: rgba(244, 106, 106, 0.18);
}

.badge.bg-soft-secondary {
  color: #74788d;
  background-color: rgba(116, 120, 141, 0.18);
}

/* Text colors */
.text-warning {
  color: #f1b44c !important;
}

.text-info {
  color: #50a5f1 !important;
}

.text-success {
  color: #34c38f !important;
}

.text-danger {
  color: #f46a6a !important;
}

.text-primary {
  color: #556ee6 !important;
}

.text-secondary {
  color: #74788d !important;
}

/* Card styling */
.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid rgba(0, 0, 0, 0.125);
}

.card-title {
  color: #495057;
  font-weight: 600;
}

/* Responsive */
@media (max-width: 768px) {
  .info-row {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .info-row strong {
    min-width: auto;
    margin-bottom: 0.25rem;
  }
  
  .result-item {
    flex-direction: column;
  }
  
  .result-name {
    min-width: auto;
    margin-bottom: 0.25rem;
  }
  
  .signature-line {
    width: 150px;
  }
}
</style>
